<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    appendTo="body"
    :base-z-index="1300"
    :closable="!saving"
    :dismissableMask="!saving"
    :draggable="false"
    :style="{ width: '26rem' }"
    :breakpoints="{ '960px': '90vw', '575px': '98vw' }"
    :pt="crcFormDialogPt"
    @hide="onClose"
  >
    <template #header>
      <div class="crc-form-dialog-header">
        <div
          class="crc-form-dialog-header__icon bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
        >
          <i :class="['pi', iconClass, 'text-base']" />
        </div>
        <div class="crc-form-dialog-header__text">
          <div class="crc-form-dialog-header__title">
            {{ isEdit ? editTitle : createTitle }}
          </div>
        </div>
      </div>
    </template>

    <form @submit.prevent="handleSubmit">
      <section class="crc-form-dialog-section">
        <div class="crc-form-dialog-fields">
          <FloatLabel v-if="showGroup" variant="on" class="w-full">
            <Select
              inputId="ref-group"
              v-model="draft.group"
              :options="groupOptions"
              option-label="label"
              option-value="value"
              :disabled="saving"
              class="w-full"
              inputClass="w-full rounded-md"
            />
            <label for="ref-group" class="text-sm font-medium">Group</label>
          </FloatLabel>

          <FloatLabel variant="on" class="w-full">
            <InputText
              id="ref-label"
              v-model="draft.label"
              :disabled="saving"
              maxlength="160"
              autocomplete="off"
              :invalid="submitted && !draft.label?.trim()"
              class="w-full rounded-md"
              :placeholder="labelPlaceholder"
            />
            <label for="ref-label" class="text-sm font-medium">
              Label <span class="text-red-500">*</span>
            </label>
          </FloatLabel>
          <small v-if="submitted && !draft.label?.trim()" class="text-xs text-red-500 pl-1">
            Label is required.
          </small>

          <FloatLabel variant="on" class="w-full">
            <InputNumber
              inputId="ref-sort"
              v-model="draft.sortOrder"
              :disabled="saving"
              :useGrouping="false"
              class="w-full"
              inputClass="w-full rounded-md"
            />
            <label for="ref-sort" class="text-sm font-medium">Sort order</label>
          </FloatLabel>
        </div>
      </section>

      <section v-if="isEdit" class="crc-form-dialog-section">
        <div class="flex items-center justify-between gap-3">
          <div>
            <div class="text-sm font-medium">Active</div>
            <div class="text-xs text-surface-500">Inactive items are hidden from form dropdowns</div>
          </div>
          <ToggleSwitch v-model="draft.active" :disabled="saving" />
        </div>
      </section>

      <div class="crc-form-dialog-footer">
        <div class="crc-form-dialog-footer__actions">
          <Button
            type="button"
            label="Cancel"
            severity="secondary"
            text
            :disabled="saving"
            @click="visibleProxy = false"
          />
          <Button type="submit" label="Save" icon="pi pi-check" :loading="saving" />
        </div>
      </div>
    </form>
  </Dialog>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import FloatLabel from 'primevue/floatlabel';
import ToggleSwitch from 'primevue/toggleswitch';
import { crcFormDialogPt } from '@/components/common/dialog/crcDialogPt.js';

defineOptions({ name: 'SimpleReferenceEditModal' });

const props = defineProps({
  visible: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  createTitle: { type: String, default: 'Add Item' },
  editTitle: { type: String, default: 'Edit Item' },
  labelPlaceholder: { type: String, default: 'Name' },
  iconClass: { type: String, default: 'pi-list' },
  showGroup: { type: Boolean, default: false },
  groupOptions: { type: Array, default: () => [] },
  defaultGroup: { type: String, default: 'other' }
});

const emit = defineEmits(['update:visible', 'save']);

const submitted = ref(false);

const draft = reactive({
  id: '',
  label: '',
  sortOrder: null,
  active: true,
  group: props.defaultGroup
});

const isEdit = computed(() => !!draft.id);

const visibleProxy = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
});

function resetDraft() {
  draft.id = '';
  draft.label = '';
  draft.sortOrder = null;
  draft.active = true;
  draft.group = props.defaultGroup;
  submitted.value = false;
}

function openCreate(defaults = {}) {
  resetDraft();
  if (defaults.group) draft.group = defaults.group;
  visibleProxy.value = true;
}

function openEdit(item) {
  resetDraft();
  draft.id = item?.id || '';
  draft.label = item?.label || '';
  draft.sortOrder = item?.sortOrder ?? null;
  draft.active = item?.active !== false;
  draft.group = item?.group || props.defaultGroup;
  visibleProxy.value = true;
}

function onClose() {
  resetDraft();
}

function handleSubmit() {
  submitted.value = true;
  if (!draft.label?.trim()) return;

  const payload = {
    id: draft.id || null,
    label: draft.label.trim()
  };
  if (draft.sortOrder != null) {
    payload.sortOrder = draft.sortOrder;
  }
  if (props.showGroup) {
    payload.group = draft.group || props.defaultGroup;
  }
  if (isEdit.value) {
    payload.active = !!draft.active;
  }
  emit('save', payload);
}

defineExpose({ openCreate, openEdit });
</script>
